import 'animate.css';

import { Grid } from '@mui/material';
import { validateEmail, validatePhoneNumber } from '@nx-smartmonkey/shared/helpers';
import { Checkbox, RoutalInputPhone, RoutalInputPhoneValue, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import * as routes from '../../../constants/Routing';
import { useRegisterOrganization } from '../../../hooks/organization/useRegisterOrganization';
import { useRetrieveSessionCountry } from '../../../hooks/session/useRetrieveSessionCountry';
import Button from '../../../react-components/Button';
import InputText from '../../../react-components/InputText';
import { PasswordInput, validatePassword } from '../../../react-components/PasswordInput';
import Text from '../../../react-components/Text';
import { AuthBody } from '../Auth.styled';

interface RegisterFormProps {
  onRegisterSuccess: (name: string, email: string) => void;
}

export const RegisterForm = ({ onRegisterSuccess }: RegisterFormProps) => {
  const intl = useIntl();
  const { countryCode } = useRetrieveSessionCountry();

  const { success, isLoading, run: register } = useRegisterOrganization();

  const [firstName, setFirstName] = useState<string>(``);
  const [lastName, setLastName] = useState<string>(``);
  const [email, setEmail] = useState<string>(``);
  const [phoneNumber, setPhoneNumber] = useState<string>(``);
  const [password, setPassword] = useState<string>(``);
  const [terms, setTerms] = useState<boolean>(false);

  useEffect(() => {
    if (success) {
      onRegisterSuccess(`${firstName} ${lastName}`, email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  const handleRegister = () => {
    if (!isFormDisabled()) {
      register({
        firstName,
        lastName,
        email,
        password,
        phone: phoneNumber,
      });
    }
  };

  const isFormDisabled = () =>
    isLoading ||
    (firstName ?? ``) === `` ||
    (lastName ?? ``) === `` ||
    (email ?? ``) === `` ||
    (password ?? ``) === `` ||
    !terms ||
    !validatePhoneNumber(phoneNumber) ||
    !validatePassword(password, email).isValid;

  return (
    <AuthBody container direction="column" justifyContent="center">
      <Grid container justifyContent="center">
        <Text intlId="auth.register" variant="h5" />
      </Grid>

      <VSpacer large />

      <Grid
        container
        direction="column"
        justifyContent="center"
        style={{ minHeight: `250px` }}
        className="animate__animated animate__fadeIn"
      >
        <Grid item>
          <Text variant="body" intlId="auth.register.first_name" />

          <VSpacer small />

          <InputText
            id="first_name"
            type="text"
            placeholder={intl.formatMessage({ id: `none` })}
            fieldValue={firstName}
            onChange={(text: any) => setFirstName(text)}
            size="medium"
            variant="outlined"
            onKeyDown={(ev) => {
              if (ev.keyCode === 13) {
                handleRegister();
              }
            }}
            inputContainerStyles={{
              width: `100%`,
            }}
            inputProps={{
              style: {
                minHeight: `24px`,
              },
            }}
            required
            validators={[
              {
                errorMessage: (
                  <Text tagName="span" style={{ color: RoutalPalette.danger }} intlId="validation.mandatory_field" />
                ),
                isInvalid: (text?: string) => (text ?? ``).length === 0,
              },
            ]}
          />
        </Grid>

        <VSpacer small />

        <Grid item>
          <Text variant="body" intlId="auth.register.last_name" />

          <VSpacer small />

          <InputText
            id="last_name"
            type="text"
            placeholder={intl.formatMessage({ id: `none` })}
            fieldValue={lastName}
            onChange={(text: any) => setLastName(text)}
            size="medium"
            variant="outlined"
            onKeyDown={(ev) => {
              if (ev.keyCode === 13) {
                handleRegister();
              }
            }}
            inputContainerStyles={{
              width: `100%`,
            }}
            inputProps={{
              style: {
                minHeight: `24px`,
              },
            }}
            required
            validators={[
              {
                errorMessage: (
                  <Text tagName="span" style={{ color: RoutalPalette.danger }} intlId="validation.mandatory_field" />
                ),
                isInvalid: (text?: string) => (text ?? ``).length === 0,
              },
            ]}
          />
        </Grid>

        <VSpacer small />

        <Grid item>
          <Text variant="body" intlId="auth.register.email" />

          <VSpacer small />

          <InputText
            id="email"
            type="email"
            placeholder={intl.formatMessage({ id: `none` })}
            fieldValue={email}
            onChange={(mail: any) => setEmail(mail)}
            size="medium"
            variant="outlined"
            onKeyDown={(ev) => {
              if (ev.keyCode === 13) {
                handleRegister();
              }
            }}
            inputContainerStyles={{
              width: `100%`,
            }}
            inputProps={{
              style: {
                minHeight: `24px`,
              },
            }}
            required
            validators={[
              {
                errorMessage: (
                  <Text tagName="span" style={{ color: RoutalPalette.danger }} intlId="validation.invalid_email" />
                ),
                isInvalid: (text?: string) => !text || !validateEmail(text),
              },
            ]}
          />
        </Grid>

        <VSpacer small />

        <Grid item>
          <Text variant="body" intlId="auth.register.phone" />

          <VSpacer small />

          <RoutalInputPhone
            countryCallingCodeEditable={false}
            focusInputOnCountrySelection
            international
            numberInputProps={{
              style: {
                height: `27px`,
              },
            }}
            placeholder={intl.formatMessage({ id: `none` })}
            defaultCountry={countryCode}
            value={phoneNumber as RoutalInputPhoneValue}
            onChange={(value?: RoutalInputPhoneValue) => {
              setPhoneNumber(value ?? ``);
            }}
            on
          />
        </Grid>

        <VSpacer small />

        <Grid item>
          <Text variant="body" intlId="auth.register.password" />

          <VSpacer small />

          <PasswordInput
            id="password"
            type="password"
            placeholder={intl.formatMessage({ id: `none` })}
            fieldValue={password}
            onChange={(psw: any) => setPassword(psw)}
            size="medium"
            variant="outlined"
            required
            withEye
            email={email}
            onKeyDown={(ev) => {
              if (ev.keyCode === 13) {
                handleRegister();
              }
            }}
            inputContainerStyles={{
              width: `100%`,
            }}
            inputProps={{
              style: {
                minHeight: `24px`,
              },
            }}
          />
        </Grid>

        <VSpacer />

        <Grid container direction="row" wrap="nowrap" alignItems="center">
          <Checkbox
            checked={terms}
            onChange={(e: any, value: any) => {
              setTerms(value);
            }}
          />
          <Text
            style={{ fontSize: `12px`, paddingLeft: `8px` }}
            intlId="auth.register.terms"
            intlValues={{
              a: (chunks: any) => (
                <a
                  style={{ fontSize: `12px` }}
                  href={`${routes.EXTERNAL.LANDING}aviso-legal`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {chunks}
                </a>
              ),
            }}
          />
        </Grid>

        <VSpacer medium />

        <Grid container alignItems="center" direction="row" justifyContent="center">
          <Button
            id="register_next"
            buttonType="button"
            type="submit"
            disabled={isFormDisabled()}
            onClick={handleRegister}
            size="large"
            intlId="auth.register.submit"
            style={{
              width: `100%`,
            }}
          />
        </Grid>
      </Grid>
    </AuthBody>
  );
};
